import { type ReactElement, type ReactNode } from 'react';
import Link from '@precis-digital/kurama/Link';
import { type TypographyVariant } from '@precis-digital/kurama/Typography';
import { MarkdownCode, MarkdownPre } from './components/MarkdownCode';
export type MarkdownVariant = 'compact' | 'standard';
const MARKDOWN_LINK_TYPOGRAPHY_VARIANT: Record<MarkdownVariant, TypographyVariant> = {
  compact: 'body3',
  standard: 'body2'
};
interface MarkdownCodeComponentProps {
  node?: unknown;
  inline?: boolean;
  className?: string;
  children?: ReactNode;
}
interface MarkdownPreComponentProps {
  node?: unknown;
  children?: ReactNode;
}
interface MarkdownEmphasisComponentProps {
  children?: ReactNode;
}
interface MarkdownAnchorComponentProps {
  node?: unknown;
  href?: string;
  className?: string;
  children?: ReactNode;
}
export interface MarkdownElementComponents {
  code: (props: MarkdownCodeComponentProps) => ReactElement;
  pre: (props: MarkdownPreComponentProps) => ReactElement;
  strong: (props: MarkdownEmphasisComponentProps) => ReactElement;
  em: (props: MarkdownEmphasisComponentProps) => ReactElement;
  a: (props: MarkdownAnchorComponentProps) => ReactElement;
}

/**
 * Element components shared by the full Markdown renderer and the lightweight
 * inline markdown helper. Keep element rendering in sync by changing this map.
 */
export const createMarkdownElementComponents = (variant: MarkdownVariant): MarkdownElementComponents => ({
  code: ({
    node,
    inline,
    className,
    children,
    ...codeProps
  }: MarkdownCodeComponentProps) => <MarkdownCode node={node} inline={inline} className={className} variant={variant} {...codeProps}>
      {children}
    </MarkdownCode>,
  pre: ({
    node,
    children,
    ...preProps
  }: MarkdownPreComponentProps) => <MarkdownPre node={node} variant={variant} {...preProps}>
      {children}
    </MarkdownPre>,
  strong: ({
    children
  }: MarkdownEmphasisComponentProps) => <strong>{children}</strong>,
  em: ({
    children
  }: MarkdownEmphasisComponentProps) => <em>{children}</em>,
  a: ({
    node: _node,
    href,
    className,
    children
  }: MarkdownAnchorComponentProps) => <Link href={href} className={className} variant={MARKDOWN_LINK_TYPOGRAPHY_VARIANT[variant]} target="_blank" rel="noopener noreferrer">
      {children}
    </Link>
});
const markdownElementComponentsByVariant: Record<MarkdownVariant, MarkdownElementComponents> = {
  compact: createMarkdownElementComponents('compact'),
  standard: createMarkdownElementComponents('standard')
};
export const getMarkdownElementComponents = (variant: MarkdownVariant = 'compact'): MarkdownElementComponents => markdownElementComponentsByVariant[variant];